import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  InputLabel,
  Paper,
  Slide,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useBatchUpdateItems } from 'api/aws/batch/useBatchUpdateItems'
import { useSyncQueryCache } from 'api/aws/organizations_table/useSyncQueryCache'
import { useEntitiesFromSearch } from 'api/search'
import { formatCamelCase } from 'components/DataGrid/operators/formatCamelCase'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

const FIELD_META = [
  { key: 'googleAdsId', type: 'text' },
  { key: 'googleAdsCustomerId', type: 'text' }, // Customer ID from URLs (for activity tracking)
  { key: 'metaAccountId', type: 'text' },
  { key: 'metaPixelId', type: 'text' },
  { key: 'tiktokAccountId', type: 'text' },
  { key: 'tiktokPixelId', type: 'text' },
  { key: 'simplifiOrgId', type: 'text' },
  { key: 'bingAccountId', type: 'text' },
  { key: 'bingUetId', type: 'text' },
  { key: 'websiteTemplateUrl', type: 'text' },
  { key: 'websitePages', type: 'number' },
  { key: 'websiteNotes', type: 'multiline' },
  { key: 'trackedConversions', type: 'multiline' },
] as const
type FieldKey = typeof FIELD_META[number]['key']

export default function OrgAccountIds() {
  const { PK } = useParams()
  const qc = useQueryClient()
  const theme = useTheme()
  const { organizations } = useEntitiesFromSearch()
  const organization = useMemo(() => organizations?.find(o => o?.PK === PK) ?? null, [organizations, PK])

  const { mutateAsync: batchUpdateItems } = useBatchUpdateItems(['organization', PK])
  const { syncOrganizationsCache } = useSyncQueryCache()
  const { showSnackbar } = useSnackbarContext()

  // edit mode + field state (hooks always called)
  const [isEditing, setIsEditing] = useState(false)
  const [pristine, setPristine] = useState<Record<string, any>>({})
  const [draft, setDraft] = useState<Record<string, any>>({})

  // Seed baselines from the organization (guard inside the effect)
  useEffect(() => {
    if (!organization) return
    const seed: Record<string, any> = {}
    FIELD_META.forEach(({ key }) => (seed[key] = organization?.[key as keyof typeof organization] ?? ''))
    setPristine(seed)
    // adopt server values into draft only when not actively editing
    setDraft(prev => (isEditing ? prev : seed))
  }, [organization?.PK, organization?.SK, organizations, isEditing])

  // dirty keys
  const dirtyKeys = useMemo(
    () => FIELD_META.map(({ key }) => key).filter(k => (draft?.[k] ?? '') !== (pristine?.[k] ?? '')),
    [draft, pristine]
  )
  const dirty = dirtyKeys.length > 0

  const setField = (key: FieldKey, value: any) => {
    setDraft(prev => ({ ...prev, [key]: value }))
  }

  // rows for UI (don’t depend on early return)
  const toDisplayRows = useMemo(() => {
    if (!organization && !isEditing) return [] // nothing to show yet
    if (!isEditing) {
      return FIELD_META.map(({ key }) => ({ key, value: organization?.[key as keyof typeof organization] })).filter(
        r => r.value !== undefined && r.value !== null && String(r.value).trim() !== ''
      )
    }
    // editing: show all fields (from draft)
    return FIELD_META.map(({ key }) => ({ key, value: draft[key] ?? '' }))
  }, [isEditing, draft, organization])

  const handleSave = async () => {
    if (!dirty || !organization?.PK || !organization?.SK) return

    const updates = dirtyKeys.map(k => {
      const meta = FIELD_META.find(m => m.key === k)
      const isNum = meta?.type === 'number'
      const newValue = isNum ? (draft[k] === '' ? '' : Number(draft[k])) : draft[k]
      return {
        tableName: 'ORGANIZATIONS_TABLE',
        PK: organization.PK,
        SK: organization.SK,
        fieldName: k,
        newValue,
      }
    })

    try {
      await batchUpdateItems({ updates })
      const patch: Record<string, any> = {}
      dirtyKeys.forEach(k => (patch[k] = updates.find(u => u.fieldName === k)!.newValue))
      syncOrganizationsCache(qc, { ...organization, ...patch })
      setPristine(draft) // new baseline -> clears dirty
      setIsEditing(false)
      showSnackbar('Organization account IDs updated', 'success')
    } catch {
      showSnackbar('Failed to update organization IDs', 'error')
    }
  }

  const handleCancel = () => {
    setDraft(pristine)
    setIsEditing(false)
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        height: '100%',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        bgcolor: 'transparent',
        position: 'relative',
      }}
    >
      {/* Header / Edit toggle */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2">Integration IDs</Typography>

        {!isEditing ? (
          <>
            {organization ? (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => setIsEditing(true)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <IconButton size="small" disabled>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </>
        ) : (
          <Tooltip title="Close editing">
            <IconButton size="small" onClick={handleCancel}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* Sticky save bar */}
      <Slide direction="up" in={isEditing && dirty} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'background.paper',
            border: theme => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            px: 1.5,
            py: 0.75,
            mb: 1,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2">
              {dirtyKeys.length} change{dirtyKeys.length === 1 ? '' : 's'} not saved
            </Typography>
            <Box>
              <Button size="small" onClick={handleCancel} sx={{ mr: 0.5 }}>
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={handleSave}>
                Save
              </Button>
            </Box>
          </Stack>
        </Box>
      </Slide>

      {/* Body */}
      {!organization && !isEditing ? (
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Loading organization…
          </Typography>
        </Box>
      ) : toDisplayRows.length === 0 && !isEditing ? (
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No IDs assigned
          </Typography>
        </Box>
      ) : (
        <Stack
          spacing={1}
          divider={<Divider flexItem />}
          sx={{
            overflowY: 'auto',
            pr: 0.5,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              boxShadow:
                theme.palette.mode === 'dark' ? 'inset 0 0 6px rgba(0,0,0,0.3)' : 'inset 0 0 6px rgba(255,255,255,0.3)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor:
                theme.palette.mode === 'dark' ? theme.palette.background.scrollbar_thumb : 'rgba(0,0,0,0.5)',
              borderRadius: '6px',
            },
          }}
        >
          {toDisplayRows.map(({ key, value }) => {
            const meta = FIELD_META.find(m => m.key === key)!
            const label = formatCamelCase(String(key))
            if (!isEditing) {
              return (
                <Grid key={key} container alignItems="center" columns={12} sx={{ minHeight: 34 }}>
                  <Grid item xs={6}>
                    <InputLabel shrink sx={{ m: 0, fontSize: 16, color: 'text.secondary' }}>
                      {label}
                    </InputLabel>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word', fontWeight: 500 }}>
                      {String(value)}
                    </Typography>
                  </Grid>
                </Grid>
              )
            }

            return (
              <Grid key={key} container alignItems="center" columns={12} sx={{ py: 0.5 }}>
                <Grid item xs={5} sx={{ display: 'flex', alignItems: 'center' }}>
                  <InputLabel shrink sx={{ m: 0, fontSize: 16, color: 'text.secondary' }}>
                    {label}
                  </InputLabel>
                </Grid>
                <Grid item xs={7}>
                  <TextField
                    fullWidth
                    size="small"
                    type={meta.type === 'number' ? 'number' : 'text'}
                    value={draft[key] ?? ''}
                    onChange={e => setField(key as FieldKey, meta.type === 'number' ? e.target.value : e.target.value)}
                    multiline={meta.type === 'multiline'}
                    minRows={meta.type === 'multiline' ? 3 : undefined}
                    placeholder={meta.type === 'number' ? '0' : ''}
                    inputProps={meta.type === 'number' ? { inputMode: 'numeric', pattern: '[0-9]*' } : undefined}
                  />
                </Grid>
              </Grid>
            )
          })}
        </Stack>
      )}
    </Paper>
  )
}
